import { useEffect, useRef } from "react";

import { useAppDispatch } from "app/hooks";
import { PracticeCall, useLazyGetLiveCallQuery } from "services/dialstack";

import { IncomingCallPop, popReceived } from "../incoming-call-pop.slice";
import type { InboundCallMatch } from "./use-inbound-call-pops.hook";

// The pop normally lands within a second of the ring; asking any sooner
// would race it for nothing.
export const POP_RECOVERY_DELAY_MS = 1500;
// The webhook that writes the row may still be in flight on the first try.
export const POP_RECOVERY_RETRY_DELAY_MS = 3000;
export const POP_RECOVERY_MAX_ATTEMPTS = 3;

// The pop the backend would have sent for this call, rebuilt from the row.
// An ambiguous number match (several patients, none on the row) comes back
// as no match; a real pop that lands later replaces it.
export function popFromLiveCall(
  sdkCallId: string,
  call: PracticeCall,
  receivedAt: number
): IncomingCallPop {
  return {
    callId: sdkCallId,
    backendCallId: call.id,
    clinicId: call.clinic ?? null,
    fromNumber: call.from_number,
    fromName: call.from_name || null,
    matchedOn: call.matched_on,
    patientIds: call.patient ? [call.patient.id] : [],
    contact: call.contact ?? null,
    receivedAt
  };
}

// Fills in the screen pop for a live inbound call that has none (ENG-2380).
// The pop is the one way the softphone learns the backend Call id, and
// without it every unknown-caller action is disabled for the whole call. It
// went missing for a trial whose temporary number no clinic owned (the backend
// then sent no pop at all), and it is lost on any reload mid-call. Mounted
// once, by the widget, with every live inbound match.
export default function usePopRecovery(matches: InboundCallMatch[]) {
  const dispatch = useAppDispatch();
  const [getLiveCall] = useLazyGetLiveCallQuery();
  // Attempts made per SDK call id, and the timer while one is scheduled.
  const attempts = useRef(new Map<string, number>());
  const timers = useRef(new Map<string, number>());
  // What the latest render says, read when a timer or a response lands.
  const missingRef = useRef(new Set<string>());

  const live = matches.map((match) => match.callId);
  const missing = matches
    .filter((match) => match.backendCallId === null)
    .map((match) => match.callId);
  missingRef.current = new Set(missing);
  const liveKey = live.join("\u0000");
  const missingKey = missing.join("\u0000");

  useEffect(() => {
    const cancel = (callId: string) => {
      const timer = timers.current.get(callId);
      if (timer === undefined) return;
      window.clearTimeout(timer);
      timers.current.delete(callId);
    };
    const schedule = (callId: string, delay: number) => {
      timers.current.set(
        callId,
        window.setTimeout(async () => {
          timers.current.delete(callId);
          if (!missingRef.current.has(callId)) return;
          const made = (attempts.current.get(callId) ?? 0) + 1;
          attempts.current.set(callId, made);
          try {
            const call = await getLiveCall({
              dialstack_call_id: callId
            }).unwrap();
            // A real pop may have landed meanwhile. It knows more (every
            // candidate patient, the transfer), so it wins.
            if (!missingRef.current.has(callId)) return;
            dispatch(popReceived(popFromLiveCall(callId, call, Date.now())));
          } catch {
            if (
              made < POP_RECOVERY_MAX_ATTEMPTS &&
              missingRef.current.has(callId)
            )
              schedule(callId, POP_RECOVERY_RETRY_DELAY_MS);
          }
        }, delay)
      );
    };

    missingRef.current.forEach((callId) => {
      if (timers.current.has(callId) || attempts.current.has(callId)) return;
      schedule(callId, POP_RECOVERY_DELAY_MS);
    });
    // The pop arrived, or the call ended: nothing left to ask.
    timers.current.forEach((_, callId) => {
      if (!missingRef.current.has(callId)) cancel(callId);
    });
    const liveSet = new Set(liveKey === "" ? [] : liveKey.split("\u0000"));
    attempts.current.forEach((_, callId) => {
      if (!liveSet.has(callId)) attempts.current.delete(callId);
    });
  }, [dispatch, getLiveCall, liveKey, missingKey]);

  useEffect(
    () => () => {
      timers.current.forEach((timer) => window.clearTimeout(timer));
      timers.current.clear();
    },
    []
  );
}
